import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { PageHeader } from '@/shared/PageHeader';
import { notifyNativeUserUpdate, setNativeBypassVolumePreference, isInNativeWrapper, requestNativeVolumesDump, requestLastBgDump } from '@/services/nativeBridge';
import { useAuth } from '@/shared/AuthContext';

interface User { id: string; nickname: string; email: string; bypassVolume?: boolean; roles?: string[] }

// Tipo dell'oggetto user del context globale (vedi AuthContext.tsx)
type VvfUserShape = {
  id: string; email: string; nickname: string;
  homeTenantId?: number; roles?: string[]; approved?: boolean;
  bypassVolume?: boolean; [k: string]: unknown;
};

export function SettingsPage(): React.JSX.Element {
  usePageTitle('Impostazioni');
  const toast = useToast();
  const { user, setUser: updateAuthUser } = useAuth();
  const [nickname, setNickname] = useState('');
  const [bypassVolume, setBypassVolume] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!user) return;
    setNickname(user.nickname);
    setBypassVolume(user.bypassVolume === true);
  }, [user]);

  const save = useMutation({
    mutationFn: async () => {
      if (newPassword && newPassword !== confirmPassword) throw new Error('Le password non coincidono');
      // Il PUT ritorna l'oggetto user aggiornato dal DB. Usiamo quello
      // come source of truth per persistere localStorage (no rischio di
      // disallineamento tra valore del form e valore del DB).
      const res = await api.put('/users/me/settings', {
        nickname, bypassVolume,
        newPassword: newPassword || null,
      });
      const updated = res.data as User;
      if (updated && updated.id) {
        // updateAuthUser propaga a tutti i useAuth consumer (audit #8)
        // e mantiene localStorage in sync atomicamente.
        updateAuthUser(updated as unknown as VvfUserShape);
        setBypassVolume(updated.bypassVolume === true);
        notifyNativeUserUpdate(updated as unknown as Record<string, unknown>);
        // Native module VvfAudio: ricrea i 2 channel selettiva con
        // AudioAttributes.USAGE_ALARM (max volume sveglia, bypass volume
        // notifiche utente) se bypassVolume e' true.
        setNativeBypassVolumePreference(updated.bypassVolume === true);
      }
      // Bryan: la preferenza bypassVolume riguarda solo il volume,
      // NON il "Non disturbare". L'utente loggato in mobile sente
      // la sirena in-app a volume 1.0 (expo-av) se il toggle e' attivo.
      // Per il channel sound Android killata serve sviluppo nativo
      // (USAGE_ALARM) — limite Expo managed, non implementato.
    },
    onSuccess: () => {
      toast.show('Impostazioni salvate', 'success');
      setNewPassword(''); setConfirmPassword('');
    },
    onError: (err: Error) => toast.show(err.message ?? 'Errore salvataggio', 'error'),
  });

  if (!user) return <div className="page-container">Caricamento...</div>;

  return (
    <div className="page-container">
      <PageHeader title="⚙️ Impostazioni" />

      <div style={{ background: '#fff', borderRadius: 10, padding: 24, marginTop: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <h3 style={{ marginTop: 0, color: '#333' }}>Profilo</h3>
        <div style={{ display: 'grid', gap: 16, maxWidth: 600 }}>
          <label>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Email</div>
            <input type="email" value={user.email} disabled
              style={{ width: '100%', padding: 10, border: '1px solid #ced4da', borderRadius: 6, background: '#f8f9fa', color: '#666' }} />
          </label>
          <label>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Nickname</div>
            <input type="text" value={nickname} onChange={e => setNickname(e.target.value)}
              style={{ width: '100%', padding: 10, border: '1px solid #ced4da', borderRadius: 6 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={bypassVolume} onChange={e => setBypassVolume(e.target.checked)}
              style={{ width: 18, height: 18 }} />
            <span>
              <strong>Suoneria selettiva al massimo volume</strong> — quando il telefono
              non è silenziato, la sirena suona al massimo anche se il volume di sistema
              è basso. In <strong>silenzioso/vibrazione</strong> la selettiva fa solo
              vibrare; in <strong>&quot;Non disturbare&quot;</strong> non suona.
              (Nessun permesso speciale richiesto.)
            </span>
          </label>
          <AudioDiagnosticButton />
        </div>

        <h3 style={{ marginTop: 32, color: '#333', borderTop: '1px solid #eee', paddingTop: 20 }}>Cambio password</h3>
        <div style={{ display: 'grid', gap: 16, maxWidth: 600 }}>
          <label>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Nuova password</div>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Lascia vuoto per non cambiare"
              style={{ width: '100%', padding: 10, border: '1px solid #ced4da', borderRadius: 6 }} />
          </label>
          <label>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Conferma nuova password</div>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              style={{ width: '100%', padding: 10, border: '1px solid #ced4da', borderRadius: 6 }} />
          </label>
        </div>

        <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={() => save.mutate()} disabled={save.isPending || !nickname}
            style={{ padding: '12px 28px', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer',
                     background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.3)' }}>
            💾 Salva impostazioni
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper formato leggibile per un dump audio (live o storico).
 * Aggiunge prefisso ts se passato (per snapshot bg).
 */
function formatAudioDump(dump: { music: number; musicMax: number; alarm: number; alarmMax: number; notification: number; notificationMax: number; ringerMode: number; notificationPolicyGranted: number; appState?: string }, tsLabel?: string): string {
  const ringer = dump.ringerMode === 0 ? '🔇 SILENZIOSO'
               : dump.ringerMode === 1 ? '📳 VIBRAZIONE'
               : '🔔 NORMALE';
  const policy = dump.notificationPolicyGranted === 1
    ? '✅ Concesso'
    : '❌ MANCANTE — la sirena resta muta in silenzioso! Vai in Impostazioni Android → Suono → Non disturbare → Accesso app → VVF Tracker';
  return [
    tsLabel ? `📌 ${tsLabel}` : '',
    dump.appState ? `App state al trigger: ${dump.appState}` : '',
    tsLabel ? '' : null,
    `Modalità suoneria: ${ringer}`,
    `Permesso DnD access: ${policy}`,
    '',
    `Volume Sveglia (channel sound): ${dump.alarm} / ${dump.alarmMax}`,
    `Volume Multimediale (in-app): ${dump.music} / ${dump.musicMax}`,
    `Volume Notifiche: ${dump.notification} / ${dump.notificationMax}`,
    '',
    dump.ringerMode !== 2
      ? '⚠️ Device NON in modalità normale — il channel sound viene soppresso.'
      : '✓ Device in modalità normale, channel sound abilitato.',
    dump.alarm === 0
      ? '⚠️ Volume Sveglia a ZERO — anche se ringerMode fosse OK, il channel suonerebbe muto.'
      : '',
  ].filter(s => s !== null && s !== '').join('\n');
}

/**
 * Bottoni diagnostici audio — visibili solo dentro il wrapper RN mobile.
 *
 *  - "Diagnostica audio (ora)": legge lo stato CORRENTE — utile per
 *    verificare config dopo aver attivato bypass o concesso DnD access.
 *  - "Ultimo dump background": legge lo SNAPSHOT salvato all'ultimo arrivo
 *    di una FCM selective.created (catturato anche se l'app era in bg warm).
 *    Cosi' Bryan puo' capire perche' "in background non suona" senza dover
 *    avere l'app in foreground al momento del trigger.
 */
function AudioDiagnosticButton(): React.JSX.Element | null {
  if (!isInNativeWrapper()) return null;
  async function showLive(): Promise<void> {
    const dump = await requestNativeVolumesDump();
    if (!dump) {
      window.alert('Impossibile leggere lo stato audio (timeout 3s).\n\nPossibili cause:\n• Bundle Metro stale (rebuild APK)\n• Modulo VvfAudio non caricato');
      return;
    }
    window.alert('=== STATO AUDIO ATTUALE ===\n\n' + formatAudioDump(dump));
  }
  async function showLastBg(): Promise<void> {
    const res = await requestLastBgDump();
    if (!res || !res.data) {
      window.alert('Nessun dump di background salvato.\n\n' +
        'Lo snapshot viene catturato all\'arrivo di una FCM "selective.created".\n' +
        'Aspetta che il dispatcher mandi una selettiva (anche con app in background) e riprova.');
      return;
    }
    const ts = new Date(res.ts ?? '').toLocaleString('it-IT');
    window.alert('=== STATO AUDIO QUANDO E\' ARRIVATA LA SELETTIVA ===\n\n' +
      formatAudioDump(res.data, `Trigger: ${ts}`));
  }
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
      <button
        type="button"
        onClick={showLive}
        style={{
          padding: '8px 16px', fontSize: 13, fontWeight: 600,
          border: '1px solid #ced4da', borderRadius: 6, cursor: 'pointer',
          background: '#f8f9fa', color: '#333',
        }}>
        🔍 Diagnostica audio (ora)
      </button>
      <button
        type="button"
        onClick={showLastBg}
        style={{
          padding: '8px 16px', fontSize: 13, fontWeight: 600,
          border: '1px solid #ced4da', borderRadius: 6, cursor: 'pointer',
          background: '#fff3cd', color: '#856404',
        }}>
        📋 Ultimo dump background
      </button>
    </div>
  );
}
